import {
  App,
  Alert,
  Button,
  Card,
  Checkbox,
  Divider,
  Skeleton,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  rolesApi,
  PERMISSION_ACTIONS,
  type AppModuleDto,
  type PermissionAction,
} from '@/api/users';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

interface Row {
  module: AppModuleDto;
  granted: Record<PermissionAction, boolean>;
}

const ACTION_LABEL: Record<PermissionAction, string> = {
  VIEW: 'View',
  CREATE: 'Create',
  EDIT: 'Edit',
  APPROVE: 'Approve',
  DELETE: 'Delete',
};

export function RolePermissionsPage() {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const roleId = Number(params.id);
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canEdit = useAuthStore((s) => s.hasAuthority('USER_MGMT:EDIT'));

  const queries = useQueries({
    queries: [
      { queryKey: ['users', 'roles'], queryFn: rolesApi.list, staleTime: 5 * 60 * 1000 },
      { queryKey: ['users', 'modules'], queryFn: rolesApi.modules, staleTime: 5 * 60 * 1000 },
      {
        queryKey: ['users', 'permissions', roleId],
        queryFn: () => rolesApi.permissions(roleId),
        enabled: Number.isFinite(roleId),
      },
      {
        queryKey: ['users', 'capabilities', 'catalog'],
        queryFn: rolesApi.capabilityCatalog,
        staleTime: 5 * 60 * 1000,
      },
      {
        queryKey: ['users', 'capabilities', roleId],
        queryFn: () => rolesApi.capabilities(roleId),
        enabled: Number.isFinite(roleId),
      },
    ],
  });
  const [rolesQ, modulesQ, grantsQ, capCatalogQ, capGrantsQ] = queries;
  const role = rolesQ.data?.find((r) => r.id === roleId);
  const modules = modulesQ.data;
  const initialGrants = grantsQ.data?.grants;
  const capCatalog = capCatalogQ.data;
  const initialCapCodes = capGrantsQ.data?.codes;

  const [grants, setGrants] = useState<Record<string, Set<PermissionAction>>>({});
  const [capCodes, setCapCodes] = useState<Set<string>>(new Set());
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!modules || !initialGrants) return;
    const next: Record<string, Set<PermissionAction>> = {};
    for (const m of modules) {
      next[m.code] = new Set(initialGrants[m.code] ?? []);
    }
    setGrants(next);
    setTouched(false);
  }, [modules, initialGrants]);

  useEffect(() => {
    if (!initialCapCodes) return;
    setCapCodes(new Set(initialCapCodes));
    setTouched(false);
  }, [initialCapCodes]);

  const rows: Row[] = (modules ?? [])
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((m) => ({
      module: m,
      granted: PERMISSION_ACTIONS.reduce(
        (acc, a) => ({ ...acc, [a]: grants[m.code]?.has(a) ?? false }),
        {} as Record<PermissionAction, boolean>,
      ),
    }));

  const totalChecked = rows.reduce(
    (acc, r) => acc + PERMISSION_ACTIONS.filter((a) => r.granted[a]).length,
    0,
  );

  const setCell = (code: string, action: PermissionAction, checked: boolean) => {
    setGrants((prev) => {
      const next = { ...prev };
      const current = new Set(prev[code] ?? []);
      if (checked) current.add(action);
      else current.delete(action);
      next[code] = current;
      return next;
    });
    setTouched(true);
  };

  const setRow = (code: string, allChecked: boolean) => {
    setGrants((prev) => ({
      ...prev,
      [code]: new Set(allChecked ? PERMISSION_ACTIONS : []),
    }));
    setTouched(true);
  };

  const setColumn = (action: PermissionAction, allChecked: boolean) => {
    if (!modules) return;
    setGrants((prev) => {
      const next: typeof prev = {};
      for (const m of modules) {
        const set = new Set(prev[m.code] ?? []);
        if (allChecked) set.add(action);
        else set.delete(action);
        next[m.code] = set;
      }
      return next;
    });
    setTouched(true);
  };

  const save = useMutation({
    mutationFn: async () => {
      const payload: Record<string, PermissionAction[]> = {};
      for (const code of Object.keys(grants)) {
        payload[code] = PERMISSION_ACTIONS.filter((a) => grants[code]!.has(a));
      }
      // Save matrix + capabilities together — they're conceptually one edit.
      await rolesApi.updatePermissions(roleId, payload);
      await rolesApi.updateCapabilities(roleId, Array.from(capCodes));
    },
    onSuccess: () => {
      message.success('Permissions saved');
      qc.invalidateQueries({ queryKey: ['users', 'permissions', roleId] });
      qc.invalidateQueries({ queryKey: ['users', 'capabilities', roleId] });
      setTouched(false);
    },
    onError: (e) => message.error(extractError(e)),
  });

  const toggleCap = (code: string, checked: boolean) => {
    setCapCodes((prev) => {
      const next = new Set(prev);
      if (checked) next.add(code);
      else next.delete(code);
      return next;
    });
    setTouched(true);
  };

  // Group capability catalog entries by their module code for tidy rendering.
  const capByModule = useMemo(() => {
    if (!capCatalog) return new Map<string, typeof capCatalog>();
    const m = new Map<string, typeof capCatalog>();
    for (const c of capCatalog) {
      if (!m.has(c.moduleCode)) m.set(c.moduleCode, []);
      m.get(c.moduleCode)!.push(c);
    }
    return m;
  }, [capCatalog]);

  if (rolesQ.isLoading || modulesQ.isLoading || grantsQ.isLoading
      || capCatalogQ.isLoading || capGrantsQ.isLoading) {
    return <Skeleton active paragraph={{ rows: 10 }} />;
  }

  if (!role) {
    return (
      <Alert
        type="error"
        showIcon
        message="Role not found"
        action={<Button onClick={() => navigate('/users/roles')}>Back to roles</Button>}
      />
    );
  }

  const columns: TableProps<Row>['columns'] = [
    {
      title: 'Module',
      dataIndex: ['module', 'name'],
      width: 220,
      fixed: 'left',
      render: (_: unknown, row) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text strong>{row.module.name}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {row.module.code}
          </Typography.Text>
        </Space>
      ),
    },
    ...PERMISSION_ACTIONS.map((action) => ({
      title: (
        <ColumnHeader
          label={ACTION_LABEL[action]}
          allChecked={rows.length > 0 && rows.every((r) => r.granted[action])}
          someChecked={rows.some((r) => r.granted[action])}
          disabled={!canEdit}
          onChange={(v) => setColumn(action, v)}
        />
      ),
      dataIndex: ['granted', action],
      width: 110,
      align: 'center' as const,
      render: (_: unknown, row: Row) => (
        <Checkbox
          checked={row.granted[action]}
          disabled={!canEdit}
          onChange={(e) => setCell(row.module.code, action, e.target.checked)}
        />
      ),
    })),
    {
      title: 'All',
      width: 80,
      align: 'center',
      render: (_, row) => (
        <Checkbox
          indeterminate={
            PERMISSION_ACTIONS.some((a) => row.granted[a]) &&
            !PERMISSION_ACTIONS.every((a) => row.granted[a])
          }
          checked={PERMISSION_ACTIONS.every((a) => row.granted[a])}
          disabled={!canEdit}
          onChange={(e) => setRow(row.module.code, e.target.checked)}
        />
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow={`Module / Roles · ${role.name}`}
        title={role.name}
        description={
          <Space size={6} wrap>
            {role.system && <Tag color="processing">System</Tag>}
            {!role.active && <Tag>Inactive</Tag>}
            <span>{role.description ?? 'No description'}</span>
          </Space>
        }
        count={{ value: totalChecked, label: 'granted' }}
        actions={
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/users/roles')}
            >
              Back
            </Button>
            {canEdit && (
              <Button
                type="primary"
                icon={<SaveOutlined />}
                disabled={!touched}
                loading={save.isPending}
                onClick={() => save.mutate()}
              >
                Save changes
              </Button>
            )}
          </Space>
        }
      />

      <Table
        rowKey={(r) => r.module.code}
        size="middle"
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 'max-content' }}
      />

      {/* Capabilities — fine-grained sub-permissions seeded in V10/V11/V12 */}
      <Card
        title="Sub-permissions"
        size="small"
        styles={{ body: { padding: 0 } }}
        extra={
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Fine-grained toggles that the matrix above can't express
          </Typography.Text>
        }
      >
        {(capCatalog ?? []).length === 0 ? (
          <div style={{ padding: 16 }}>
            <Typography.Text type="secondary">No sub-permissions registered.</Typography.Text>
          </div>
        ) : (
          (modules ?? [])
            .slice()
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .filter((m) => capByModule.has(m.code))
            .map((m, idx) => (
              <div key={m.code}>
                {idx > 0 && <Divider style={{ margin: 0 }} />}
                <div style={{ padding: '14px 18px' }}>
                  <Typography.Text strong style={{ fontSize: 13 }}>
                    {m.name}
                  </Typography.Text>
                  <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
                    {capByModule.get(m.code)!.map((c) => (
                      <label
                        key={c.code}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '22px 1fr',
                          gap: 10,
                          alignItems: 'start',
                          cursor: canEdit ? 'pointer' : 'default',
                        }}
                      >
                        <Checkbox
                          checked={capCodes.has(c.code)}
                          disabled={!canEdit}
                          onChange={(e) => toggleCap(c.code, e.target.checked)}
                        />
                        <div>
                          <Typography.Text>{c.label}</Typography.Text>
                          <Typography.Paragraph
                            type="secondary"
                            style={{ fontSize: 12, margin: 0 }}
                          >
                            {c.description}
                          </Typography.Paragraph>
                          <Typography.Text code style={{ fontSize: 11, opacity: 0.7 }}>
                            {c.code}
                          </Typography.Text>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            ))
            // Catch any catalog entries whose moduleCode doesn't match a known
            // module — defensive against catalog/module drift.
            .concat(
              (capCatalog ?? [])
                .filter((c) => !(modules ?? []).some((m) => m.code === c.moduleCode))
                .map((c) => (
                  <div key={c.code} style={{ padding: '14px 18px', borderTop: '1px solid var(--ant-color-border-secondary)' }}>
                    <label style={{ display: 'flex', gap: 10, alignItems: 'start' }}>
                      <Checkbox
                        checked={capCodes.has(c.code)}
                        disabled={!canEdit}
                        onChange={(e) => toggleCap(c.code, e.target.checked)}
                      />
                      <div>
                        <Typography.Text>{c.label}</Typography.Text>
                        <Typography.Paragraph type="secondary" style={{ fontSize: 12, margin: 0 }}>
                          {c.description} — <Typography.Text code style={{ fontSize: 11 }}>{c.code}</Typography.Text>
                        </Typography.Paragraph>
                      </div>
                    </label>
                  </div>
                )),
            )
        )}
      </Card>
    </div>
  );
}

function ColumnHeader({
  label,
  allChecked,
  someChecked,
  disabled,
  onChange,
}: {
  label: string;
  allChecked: boolean;
  someChecked: boolean;
  disabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <Space orientation="vertical" size={2} align="center">
      <span>{label}</span>
      <Checkbox
        indeterminate={someChecked && !allChecked}
        checked={allChecked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
    </Space>
  );
}
